/**
 * Tone is never the only carrier of meaning: every pill has a text label, and the glyph is
 * `aria-hidden` because it encodes the same fact the text already states.
 */

import type { ReactNode } from 'react';
import styles from './Pill.module.css';
import type { Tone } from './tone';

export interface PillProps {
  tone?: Tone;
  size?: 'default' | 'mini';
  emphasis?: 'default' | 'strong';
  /** A single decorative character rendered before the label. */
  glyph?: string;
  className?: string;
  title?: string;
  children: ReactNode;
}

export function Pill({
  tone = 'neutral',
  size = 'default',
  emphasis = 'default',
  glyph,
  className,
  title,
  children,
}: PillProps) {
  const classes = [
    styles.pill,
    styles[tone],
    size === 'mini' ? styles.mini : undefined,
    emphasis === 'strong' ? styles.strong : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} title={title}>
      {glyph ? (
        <span className={styles.glyph} aria-hidden="true">
          {glyph}
        </span>
      ) : null}
      {children}
    </span>
  );
}

export interface ChipProps {
  children: ReactNode;
  /** Solid inverted fill — the one that matters here. */
  emphasis?: 'default' | 'strong';
  /** Green outline — still live today. */
  outlined?: boolean;
  className?: string;
}

export function Chip({ children, emphasis = 'default', outlined = false, className }: ChipProps) {
  const classes = [
    styles.chip,
    emphasis === 'strong' ? styles.chipStrong : undefined,
    outlined ? styles.chipOutlined : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return <span className={classes}>{children}</span>;
}

export interface CountBadgeProps {
  count: number;
  active?: boolean;
  /** Folded into the parent's accessible name, so the badge itself is hidden. */
  hidden?: boolean;
}

export function CountBadge({ count, active = false, hidden = true }: CountBadgeProps) {
  return (
    <span
      className={[styles.countBadge, active ? styles.countBadgeActive : undefined]
        .filter(Boolean)
        .join(' ')}
      aria-hidden={hidden || undefined}
    >
      {count}
    </span>
  );
}
